/**
 * Phase INV-CAT — "Create invoice from rental" modal.
 *
 * Layout:
 *   1. Rental + pricing header (unchanged).
 *   2. Category dropdown — NO default. Submit gated until picked.
 *   3. If category is_prefill=true (Rent): existing period / issue
 *      date / terms / preview flow.
 *      Otherwise: Description + Quantity + Unit price + live Line total.
 *   4. Supporting files (always, max 5, 10 MB each, PDF/JPG/PNG).
 *   5. Submit → /generate-from-rental (Rent) or /admin/invoices
 *      (generic line) → upload staged attachments → navigate to detail.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Paperclip, X } from "lucide-react";

const TERMS_OPTIONS = [
    { value: "due_on_receipt", label: "Due on receipt", days: 0 },
    { value: "net_7", label: "Net 7", days: 7 },
    { value: "net_15", label: "Net 15", days: 15 },
    { value: "net_30", label: "Net 30", days: 30 },
];
const ATTACH_MAX = 5;
const ATTACH_PER_FILE_MB = 10;
const ALLOWED_MIME = ["application/pdf", "image/jpeg", "image/png"];

const todayIso = () => new Date().toISOString().slice(0, 10);
const addDays = (iso, days) => {
    const d = new Date(iso + "T00:00:00Z"); d.setUTCDate(d.getUTCDate() + days);
    return d.toISOString().slice(0, 10);
};
const daysBetween = (a, b) => {
    const da = new Date(a + "T00:00:00Z"); const db = new Date(b + "T00:00:00Z");
    return Math.round((db - da) / (1000 * 60 * 60 * 24));
};

export default function CreateInvoiceFromRentalModal({ open, onOpenChange, rental, backfillMode = false, onCreated }) {
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [categoryId, setCategoryId] = useState("");  // "" = no pick (placeholder)
    // Phase INV-DEFAULT-NET7 (2026-07-29) — mirror the backend default.
    const [terms, setTerms] = useState("net_7");
    const [issueDate, setIssueDate] = useState(todayIso());
    // Rent-path state
    const [periodStart, setPeriodStart] = useState(todayIso());
    const [periodEnd, setPeriodEnd] = useState(addDays(todayIso(), 29));
    // Non-rent state
    const [description, setDescription] = useState("");
    const [quantity, setQuantity] = useState(1);
    const [unitPriceDollars, setUnitPriceDollars] = useState("");
    // Attachments staged for upload-after-create
    const [stagedFiles, setStagedFiles] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [categoriesLoadError, setCategoriesLoadError] = useState(false);
    // Phase J.8 (2026-08-01) — backfill mode captures a mandatory
    // audit reason + defaults ``service_period_start`` to the rental's
    // ``released_at`` (rather than today). The FE toggles this on
    // automatically when the rental was released with 0 invoices; the
    // operator can also toggle it off if they want a "normal" invoice.
    const [isBackfill, setIsBackfill] = useState(false);
    const [backfillReason, setBackfillReason] = useState("");
    const [servicePeriodStart, setServicePeriodStart] = useState("");

    useEffect(() => {
        if (!open) return;
        setCategoriesLoadError(false);
        // Load categories once per open. If the fetch fails (403 on the
        // gate, network error, etc.) show a helpful placeholder rather
        // than silently rendering an empty dropdown that blocks the
        // admin from creating the invoice.
        api.get("/admin/settings/invoice-categories")
            .then((r) => {
                const list = Array.isArray(r.data) ? r.data : (r.data?.items || []);
                setCategories(list);
                if (list.length === 0) setCategoriesLoadError(true);
            })
            .catch(() => { setCategories([]); setCategoriesLoadError(true); });
        setCategoryId("");
        setStagedFiles([]);
        setDescription(""); setQuantity(1); setUnitPriceDollars("");
        setTerms("net_7");
        setIssueDate(todayIso());
        const start = todayIso();
        const end = rental?.end_date?.slice(0, 10) || addDays(start, 29);
        setPeriodStart(start);
        setPeriodEnd(addDays(start, Math.min(29, Math.max(0, daysBetween(start, end) || 29))));
        // Phase J.8 — seed backfill mode from prop + prefill the service
        // period start with the rental's released_at (or today's date
        // when released_at is missing / not parseable).
        setIsBackfill(!!backfillMode);
        setBackfillReason("");
        const relIso = (rental?.released_at || "").slice(0, 10);
        setServicePeriodStart(relIso || todayIso());
    }, [open, rental, backfillMode]);

    const selectedCat = useMemo(
        () => categories.find((c) => c.id === categoryId) || null,
        [categories, categoryId],
    );
    const isRent = !!selectedCat?.is_prefill;

    const dueDate = useMemo(() => {
        const opt = TERMS_OPTIONS.find((t) => t.value === terms) || TERMS_OPTIONS[2];
        return addDays(issueDate, opt.days);
    }, [issueDate, terms]);

    const monthsApprox = useMemo(() => {
        const days = daysBetween(periodStart, periodEnd) + 1;
        return Math.max(0, Math.round((days / 30.4375) * 100) / 100);
    }, [periodStart, periodEnd]);

    const rentPreviewAmountCents = useMemo(() => {
        if (!rental || !isRent) return 0;
        const monthly = Number(rental.monthly_rate || 0);
        if (monthly > 0) return Math.round(monthly * 100 * monthsApprox);
        const daily = Number(rental.daily_rate || 0);
        if (daily > 0) return Math.round(daily * 100 * (daysBetween(periodStart, periodEnd) + 1));
        return 0;
    }, [rental, isRent, monthsApprox, periodStart, periodEnd]);

    const lineTotalCents = useMemo(() => {
        if (isRent) return 0;
        const q = Number(quantity) || 0;
        const p = Number(unitPriceDollars) || 0;
        return Math.round(q * p * 100);
    }, [isRent, quantity, unitPriceDollars]);

    const [backfillPreviewCount, setBackfillPreviewCount] = useState(0);

    const canSubmit = useMemo(() => {
        if (submitting) return false;
        // Phase J.8d (2026-08-03) — backfill mode no longer requires
        // Category (recurring template resolves it) and skips the Rent /
        // Other line-item requirements (line items are auto-derived from
        // the rental's monthly_rate).
        if (isBackfill) {
            if (!backfillReason.trim()) return false;
            if (!servicePeriodStart) return false;
            return true;
        }
        if (!categoryId) return false;
        if (isRent) {
            return !!(periodStart && periodEnd && periodEnd >= periodStart
                && (rental?.monthly_rate || rental?.daily_rate));
        }
        return description.trim().length > 0
            && Number(quantity) > 0
            && Number(unitPriceDollars) > 0;
    }, [categoryId, submitting, isRent, periodStart, periodEnd, rental, description, quantity, unitPriceDollars, isBackfill, backfillReason, servicePeriodStart]);

    const onPickFiles = (files) => {
        const next = [...stagedFiles];
        for (const f of files) {
            if (next.length >= ATTACH_MAX) {
                toast.error(`Max ${ATTACH_MAX} files per invoice`);
                break;
            }
            if (!ALLOWED_MIME.includes(f.type)) {
                toast.error(`${f.name}: only PDF / JPG / PNG allowed`);
                continue;
            }
            if (f.size > ATTACH_PER_FILE_MB * 1024 * 1024) {
                toast.error(`${f.name}: exceeds ${ATTACH_PER_FILE_MB} MB`);
                continue;
            }
            next.push(f);
        }
        setStagedFiles(next);
    };

    const removeStaged = (idx) => {
        setStagedFiles(stagedFiles.filter((_, i) => i !== idx));
    };

    const submit = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            let draft;
            // Phase J.8b (2026-08-03) — when in backfill mode we route the
            // POST through the dedicated first-invoice endpoint. That path
            // stamps ``is_first_invoice=True`` AND fires the schedule-
            // auto-create hook (via services.billing_auto_create) so the
            // customer resumes receiving recurring monthly invoices. The
            // legacy `/admin/invoices` (+ `/generate-from-rental`) route
            // never triggered the schedule hook — that's the bug this fix
            // addresses.
            if (isBackfill) {
                // Phase J.8d hotfix (2026-08-03) — in backfill mode we
                // ALWAYS auto-derive line items from the rental's
                // monthly_rate (Category picker is hidden per J.8d, so
                // isRent-based branching would fall through to the
                // "Other" path and send an empty ``description`` → 422
                // "String should have at least 1 character"). The
                // recurring template on the backend fills in the
                // catchup periods; this first-invoice line is just the
                // seed for the walker.
                const monthly = Number(rental?.monthly_rate || 0);
                const eqLabel = rental?.equipment_type || "Equipment";
                const unit = rental?.unit_number || "—";
                const lineItems = [{
                    description: `${eqLabel} — ${unit} rental (${servicePeriodStart})`,
                    quantity: 1,
                    unit_amount_cents: Math.round(monthly * 100),
                    line_type: "rent",
                }];
                const payload = {
                    line_items: lineItems,
                    issue_date: issueDate,
                    period_start: servicePeriodStart,
                    period_end: null,
                    is_backfill: true,
                    backfill_reason: backfillReason.trim(),
                    service_period_start: servicePeriodStart,
                };
                const { data } = await api.post(
                    `/admin/rentals/${rental.id}/first-invoice/draft`,
                    payload,
                );
                draft = data;
            } else if (isRent) {
                const { data } = await api.post("/admin/invoices/generate-from-rental", {
                    rental_id: rental.id,
                    period_start: periodStart,
                    period_end: periodEnd,
                    terms,
                });
                draft = data;
                // Tag the Rent line with the category so the deletion
                // gate (409 if category in use) tracks it.
                if (draft?.line_items?.length) {
                    await api.patch(`/admin/invoices/${draft.id}`, {
                        line_items: draft.line_items.map((li) => ({
                            description: li.description,
                            quantity: li.quantity,
                            unit_amount_cents: li.unit_amount_cents,
                            line_type: li.line_type,
                            category: categoryId,
                        })),
                    });
                }
            } else {
                const payload = {
                    customer_id: rental.customer_id,
                    rental_id: rental.id,
                    terms,
                    issue_date: issueDate,
                    line_items: [{
                        description: description.trim(),
                        quantity: Number(quantity),
                        unit_amount_cents: Math.round(Number(unitPriceDollars) * 100),
                        line_type: "other",
                        category: categoryId,
                    }],
                };
                const { data } = await api.post("/admin/invoices", payload);
                draft = data;
            }
            // Upload staged attachments sequentially.
            for (const f of stagedFiles) {
                const fd = new FormData();
                fd.append("file", f);
                try {
                    await api.post(`/admin/invoices/${draft.id}/attachments`, fd,
                        { headers: { "Content-Type": "multipart/form-data" } });
                } catch (err) {
                    toast.error(`Attachment ${f.name} failed: ${formatApiError(err)}`);
                }
            }
            // Phase J.8d — Backfill in "First Invoice + Recurring
            // Catchup" mode returns a rollup we surface to the operator.
            if (isBackfill && draft?.backfill_catchup) {
                const roll = draft.backfill_catchup;
                const dollars = (roll.total_amount_cents / 100).toLocaleString(
                    undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 },
                );
                toast.success(
                    `Created ${roll.total_created} backfill invoice${roll.total_created === 1 ? "" : "s"}` +
                    ` totaling $${dollars}. Billing schedule is active.`,
                );
            } else {
                toast.success(
                    isBackfill
                        ? `Backfill draft ${draft.invoice_number} created — held for review, no email sent`
                        : `Draft ${draft.invoice_number} created`,
                );
            }
            onOpenChange(false);
            if (typeof onCreated === "function") { onCreated(draft); }
            navigate(`/admin/invoices/${draft.id}`);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setSubmitting(false); }
    };

    if (!rental) return null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-lg" data-testid="create-invoice-modal">
                <DialogHeader>
                    <DialogTitle>
                        {isBackfill ? "Create backfill invoice" : "Create invoice from rental"}
                    </DialogTitle>
                    <DialogDescription>
                        {isBackfill
                            ? "This rental was released without an invoice. Backfill draft is held for review — no email is sent automatically."
                            : "Pick a category, fill the details, optionally attach supporting files. You can fine-tune line items after creating the draft."}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-2">
                    {/* Phase J.8 — Backfill reason + service period start.
                        Shown at the top so the operator can't miss it. */}
                    {isBackfill && (
                        <div
                            data-testid="create-invoice-backfill-block"
                            className="rounded-md border border-red-200 bg-red-50 p-3 space-y-3">
                            <div className="flex items-center gap-2">
                                <input type="checkbox" checked={isBackfill}
                                    onChange={(e) => setIsBackfill(e.target.checked)}
                                    data-testid="create-invoice-backfill-toggle" />
                                <span className="text-xs font-semibold text-red-900">
                                    This is a backfill (audit-only reason required)
                                </span>
                            </div>
                            <div>
                                <Label htmlFor="bfrn">Reason <span className="text-red-500">*</span></Label>
                                <textarea
                                    id="bfrn"
                                    rows={2}
                                    value={backfillReason}
                                    onChange={(e) => setBackfillReason(e.target.value.slice(0, 500))}
                                    placeholder="Why does this backfill exist? (e.g., Released before invoice-guard shipped)"
                                    data-testid="create-invoice-backfill-reason"
                                    className="mt-1 w-full text-sm rounded-md border border-slate-200 px-2 py-1" />
                                <p className="text-[10px] text-slate-500 mt-0.5">
                                    {backfillReason.length}/500
                                </p>
                            </div>
                            <div>
                                <Label htmlFor="bfsps">Service period start <span className="text-red-500">*</span></Label>
                                <Input id="bfsps" type="date" value={servicePeriodStart}
                                    onChange={(e) => setServicePeriodStart(e.target.value)}
                                    data-testid="create-invoice-backfill-period-start" />
                                <p className="text-[10px] text-slate-500 mt-0.5">
                                    Defaults to the rental's release date.
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Rental + pricing header */}
                    <div className="rounded-md border border-slate-200 p-3 bg-slate-50 text-sm space-y-1">
                        <div className="flex justify-between">
                            <span className="text-slate-500">Rental</span>
                            <span className="font-medium text-slate-900">
                                {rental.equipment_type} {rental.unit_number ? `· ${rental.unit_number}` : ""}
                            </span>
                        </div>
                        <div className="flex justify-between">
                            <span className="text-slate-500">Pricing</span>
                            <span className="font-mono text-slate-700">
                                {rental.monthly_rate ? `$${Number(rental.monthly_rate).toLocaleString()}/mo` :
                                    rental.daily_rate ? `$${Number(rental.daily_rate).toLocaleString()}/day` :
                                        <em className="text-slate-500">No rate on rental</em>}
                            </span>
                        </div>
                    </div>

                    {/* Category picker — hidden in backfill mode (Phase J.8d 2026-08-03).
                        In backfill mode the recurring template determines the
                        line items + category, so the operator's Category pick
                        is irrelevant. */}
                    {!isBackfill && (
                    <div>
                        <Label htmlFor="cat">Category</Label>
                        <Select value={categoryId} onValueChange={setCategoryId}>
                            <SelectTrigger id="cat" data-testid="create-invoice-category">
                                <SelectValue placeholder={categoriesLoadError && categories.length === 0 ? "No categories configured — contact admin" : "Select category…"} />
                            </SelectTrigger>
                            <SelectContent>
                                {categories.map((c) => (
                                    <SelectItem key={c.id} value={c.id}
                                        data-testid={`create-invoice-category-opt-${c.id}`}>
                                        {c.label}{c.is_prefill ? " (rent prefill)" : ""}
                                    </SelectItem>
                                ))}
                                {categories.length === 0 && (
                                    <div
                                        className="px-3 py-2 text-xs text-slate-500 italic"
                                        data-testid="create-invoice-category-empty"
                                    >
                                        No categories configured — contact your super_admin.
                                    </div>
                                )}
                            </SelectContent>
                        </Select>
                        {categoriesLoadError && categories.length === 0 && (
                            <p className="mt-1 text-[11px] text-red-700" data-testid="create-invoice-category-error">
                                Unable to load categories. Try again, or ask a super_admin to configure them at Settings → Invoice categories.
                            </p>
                        )}
                    </div>
                    )}

                    {/* Rent path */}
                    {isRent && !isBackfill && (
                        <div className="space-y-3" data-testid="create-invoice-rent-block">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label htmlFor="ps">Period start</Label>
                                    <Input id="ps" type="date" value={periodStart}
                                        onChange={(e) => setPeriodStart(e.target.value)}
                                        data-testid="create-invoice-period-start" />
                                </div>
                                <div>
                                    <Label htmlFor="pe">Period end</Label>
                                    <Input id="pe" type="date" value={periodEnd}
                                        onChange={(e) => setPeriodEnd(e.target.value)}
                                        data-testid="create-invoice-period-end" />
                                </div>
                            </div>
                            <div className="rounded-md border border-blue-100 bg-blue-50/50 p-3 text-sm">
                                <p className="text-xs uppercase tracking-wider text-blue-900 font-semibold">Preview</p>
                                <p className="mt-1 text-slate-700">
                                    ≈ <span className="font-semibold">{monthsApprox.toFixed(2)} month{monthsApprox === 1 ? "" : "s"}</span> of rent
                                </p>
                                <p className="text-lg font-display font-bold text-blue-900 tabular-nums"
                                    data-testid="create-invoice-preview-total">
                                    ${(rentPreviewAmountCents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Non-rent path — hidden in backfill mode (line items
                        auto-derived from recurring template). */}
                    {categoryId && !isRent && !isBackfill && (
                        <div className="space-y-3" data-testid="create-invoice-generic-block">
                            <div>
                                <Label htmlFor="desc">Description</Label>
                                <Input id="desc" value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="What is this charge for?"
                                    data-testid="create-invoice-description" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Label htmlFor="qty">Quantity</Label>
                                    <Input id="qty" type="number" min="1" step="1"
                                        value={quantity}
                                        onChange={(e) => setQuantity(e.target.value)}
                                        data-testid="create-invoice-quantity" />
                                </div>
                                <div>
                                    <Label htmlFor="price">Unit price ($)</Label>
                                    <Input id="price" type="number" min="0" step="0.01"
                                        value={unitPriceDollars}
                                        onChange={(e) => setUnitPriceDollars(e.target.value)}
                                        placeholder="0.00"
                                        data-testid="create-invoice-unit-price" />
                                </div>
                            </div>
                            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-2 text-sm flex items-center justify-between">
                                <span className="text-emerald-900">Line total</span>
                                <span className="font-mono font-bold text-emerald-900 tabular-nums"
                                    data-testid="create-invoice-line-total">
                                    ${(lineTotalCents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                            </div>
                        </div>
                    )}

                    {/* Issue date + terms — hidden in backfill mode (drafts use
                        today + due-on-receipt via recurring template). */}
                    {categoryId && !isBackfill && (
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label htmlFor="iss">Issue date</Label>
                                <Input id="iss" type="date" value={issueDate}
                                    onChange={(e) => setIssueDate(e.target.value)}
                                    data-testid="create-invoice-issue-date" />
                            </div>
                            <div>
                                <Label htmlFor="trm">Terms</Label>
                                <Select value={terms} onValueChange={setTerms}>
                                    <SelectTrigger id="trm" data-testid="create-invoice-terms">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {TERMS_OPTIONS.map((o) => (
                                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                                <p className="text-xs text-slate-500 mt-1">Due {dueDate}</p>
                            </div>
                        </div>
                    )}

                    {/* Supporting files — hidden in backfill mode (line items
                        are auto-derived; no arbitrary attachments accepted). */}
                    {!isBackfill && (
                    <div className="rounded-md border border-dashed border-slate-300 bg-slate-50 px-3 py-2 space-y-2"
                        data-testid="create-invoice-attachments-block">
                        <Label className="flex items-center gap-1.5">
                            <Paperclip size={13} /> Supporting files
                            <span className="text-[10px] text-slate-500 font-normal">
                                (max {ATTACH_MAX}, {ATTACH_PER_FILE_MB} MB each, PDF/JPG/PNG)
                            </span>
                        </Label>
                        <input type="file" multiple
                            accept="application/pdf,image/jpeg,image/png"
                            onChange={(e) => { onPickFiles(Array.from(e.target.files || [])); e.target.value = ""; }}
                            data-testid="create-invoice-attachments-input"
                            className="text-xs" />
                        {stagedFiles.length > 0 && (
                            <ul className="text-xs space-y-1" data-testid="create-invoice-attachments-staged">
                                {stagedFiles.map((f, i) => (
                                    <li key={i} className="flex items-center justify-between bg-white rounded border border-slate-200 px-2 py-1">
                                        <span className="truncate">
                                            {f.name} <span className="text-slate-400">({Math.round(f.size / 1024)} KB)</span>
                                        </span>
                                        <button type="button" onClick={() => removeStaged(i)}
                                            className="text-slate-400 hover:text-red-700"
                                            data-testid={`create-invoice-attachment-remove-${i}`}>
                                            <X size={13} />
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    )}

                    {/* Phase J.8d (2026-08-03) — Backfill preview panel.
                        Shows the periods that WILL be created before the
                        operator submits. Refreshes whenever
                        service_period_start changes. */}
                    {isBackfill && (
                        <BackfillPreviewPanel
                            rentalId={rental.id}
                            servicePeriodStart={servicePeriodStart}
                            onCountChange={setBackfillPreviewCount}
                        />
                    )}
                </div>

                <DialogFooter>
                    <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
                    <Button onClick={submit} disabled={!canSubmit}
                        className="bg-blue-900 hover:bg-blue-800"
                        data-testid="create-invoice-submit">
                        {submitting
                            ? "Creating…"
                            : (isBackfill
                                ? (backfillPreviewCount > 1
                                    ? `Create ${backfillPreviewCount} backfill drafts`
                                    : "Create backfill draft")
                                : "Create draft")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}


// Phase J.8d (2026-08-03) — Backfill preview panel.
// Fetches the periods that WILL be created when the operator submits and
// renders a one-line-per-period summary. Refreshes when
// service_period_start changes. Debounced ~250ms so rapid date-picker
// edits don't spam the endpoint.
function BackfillPreviewPanel({ rentalId, servicePeriodStart, onCountChange }) {
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!rentalId || !servicePeriodStart) {
            setPreview(null);
            if (onCountChange) onCountChange(0);
            return;
        }
        let cancelled = false;
        const t = setTimeout(async () => {
            setLoading(true);
            setError(null);
            try {
                const { data } = await api.post(
                    `/admin/rentals/${rentalId}/first-invoice/preview-backfill`,
                    { service_period_start: servicePeriodStart },
                );
                if (!cancelled) {
                    setPreview(data);
                    if (onCountChange) onCountChange(Number(data?.total_new_periods || 0));
                }
            } catch (e) {
                if (!cancelled) setError(e);
            } finally {
                if (!cancelled) setLoading(false);
            }
        }, 250);
        return () => { cancelled = true; clearTimeout(t); };
    }, [rentalId, servicePeriodStart, onCountChange]);

    if (loading) {
        return (
            <div
                data-testid="backfill-preview-loading"
                className="rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-500">
                Computing catchup periods…
            </div>
        );
    }
    if (error) {
        return (
            <div
                data-testid="backfill-preview-error"
                className="rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                {formatApiError(error)}
            </div>
        );
    }
    if (!preview || !(preview.periods || []).length) return null;

    const totalNew = Number(preview.total_new_amount_cents || 0);
    const nCreate = Number(preview.total_new_periods || 0);
    return (
        <div
            data-testid="backfill-preview-panel"
            className="rounded-md border border-blue-200 bg-blue-50 p-3 text-sm space-y-2">
            <p className="text-xs uppercase tracking-wider font-semibold text-blue-900">
                This will create{" "}
                <span
                    data-testid="backfill-preview-count"
                    className="tabular-nums">{nCreate}</span>{" "}
                invoice{nCreate === 1 ? "" : "s"}
            </p>
            <ul className="text-xs text-blue-900/90 space-y-1">
                {preview.periods.map((p, i) => (
                    <li
                        key={`${p.scheduled_for}-${i}`}
                        data-testid={`backfill-preview-row-${i}`}
                        className="flex items-center justify-between">
                        <span>
                            <span className="font-mono">{p.scheduled_for}</span>{" "}
                            <span className="text-blue-900/60">·</span>{" "}
                            {p.kind === "first" ? "first invoice" : "catchup"}
                            {p.already_exists && (
                                <span
                                    className="ml-1 rounded bg-slate-200 px-1.5 py-0.5 text-[10px] text-slate-700"
                                    data-testid={`backfill-preview-row-${i}-exists`}>
                                    already exists — will skip
                                </span>
                            )}
                        </span>
                        <span className="font-mono tabular-nums">
                            ${(p.amount_cents / 100).toLocaleString(undefined, {
                                minimumFractionDigits: 2, maximumFractionDigits: 2,
                            })}
                        </span>
                    </li>
                ))}
            </ul>
            <div className="flex items-center justify-between text-sm border-t border-blue-200 pt-2">
                <span className="font-semibold text-blue-900">Total (new drafts)</span>
                <span
                    data-testid="backfill-preview-total"
                    className="font-mono font-bold text-blue-900 tabular-nums">
                    ${(totalNew / 100).toLocaleString(undefined, {
                        minimumFractionDigits: 2, maximumFractionDigits: 2,
                    })}
                </span>
            </div>
        </div>
    );
}
